import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { MbscCalendarEvent } from "@mobiscroll/react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { ScheduleEventBookingHeaderVisitorsContents } from "../../schedule-event-popover-header-visitors-contents";
import { ScheduleEventDeleteButton } from "../../schedule-event-delete-button.component";
import { updateNoPlaceZoneBookingScheduleAttributes } from "../../../../utils/axios-requests";
import { ScheduleSelectionBookingTab } from "./ScheduleSelection-Tab-Booking.component";
import { ScheduleSelectionInviteVisitorsTab } from "./ScheduleSelection-Tab-InviteVisitors.component";
import { TabPanel } from "../../../../features/TabPanel/TabPanel.component";
import { a11yProps } from "../../../../features/TabPanel/TabPanel.partial";
import { TransitionForSlideoutPanel } from "../../../SlideoutPanel/SlideoutPanelTransition.component";
import { useRemoteFetchColleaguesQuery } from "../../../../features/Connections/hooks/useRemoteFetchColleaguesQuery";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";
import { CustomColoredTabs } from "../../../CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../../../CustomColoredTabs/CustomColoredTab.component";
import { useRemoteFetchUserInfoForAllUsers } from "../../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { ScheduleSelectionServicesTab } from "./ScheduleSelection-Tab-Services.component";
import { NoPlaceZoneBooking } from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { scheduleSelectionDialogStyle } from "./hooks/scheduleSelectionDialogStyle.hook.function";
import { createScheduleSelectionDialogCloseHandler } from "./create-schedule-selection-dialog-close-handler.function";
import {
  buildCompanyIdByUserId,
  generateScheduleType
} from "./hooks/generateScheduleType.hook.function";
import { useConferenceServiceSetupLock } from "../../hooks/useConferenceServiceSetupLock.hook";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import { VehicleInformationCardContents } from "../../../BookingForm/vehicle-information-card-contents.component";
import { ScheduleMode } from "../../../../features/Schedule/partial/ScheduleModeToggle.partial";
import { ScheduleSelectionVisitorCardContents } from "./ScheduleSelectionVisitorCardContents.component";
import { shouldShowVisitorScheduleTab } from "../../should-show-visitor-schedule-icon.function";
import { useRemoteFetchCompanyLocations } from "../../../../hooks/Remote/Location/useRemoteFetchCompanyLocations";
import { useRemoteFetchScheduleColleagues } from "../../../../hooks/Remote/Connections/useRemoteFetchScheduleColleagues";

type P = {
  scheduleMode: ScheduleMode;
  isCalendarView: boolean;
  isEditable: boolean;
  isOpen: boolean;
  schedule: BookingScheduleInterface;
  schedulePictures: string[];
  pictureIsLoading: boolean;
  allSchedules?: BookingScheduleInterface[];
  calendarData: MbscCalendarEvent[];
  isFirstAidSelected: boolean;
  onClose: () => void;
  onCalendarDataChange?: (events: MbscCalendarEvent[]) => void;
  onScheduleChange?: (schedule: BookingScheduleInterface) => void;
  onClickUnlock?: () => void;
  refetchAllSchedule?: () => void;
  setCalendarData?: Dispatch<SetStateAction<MbscCalendarEvent[]>>;
  setDeletingBookings?: (d: boolean) => void;
  handleRejectBookings?: () => void;
  handleApproveBookings?: () => void;
};

export const ScheduleSelectionDialog: React.FC<P> = ({
  scheduleMode,
  isCalendarView,
  isEditable,
  isOpen,
  schedule,
  schedulePictures,
  pictureIsLoading,
  allSchedules,
  calendarData,
  isFirstAidSelected,
  onClose,
  onCalendarDataChange,
  onScheduleChange,
  onClickUnlock,
  refetchAllSchedule,
  setDeletingBookings,
  setCalendarData,
  handleRejectBookings,
  handleApproveBookings
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { userInformation, userRoles } = useSelector((state: RootState) => state.login);
  const isCompanyAdmin = userRoles?.isCompanyAdmin === true;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const isColleagueMode = scheduleMode === ScheduleMode.Colleague;
  /** Resource calendar: show booker identity to all authenticated users. */
  const showResourceSensitiveScheduleUi = true;

  const [selectTab, setSelectTab] = useState(0);
  const [blockCloseForTimeEdit, setBlockCloseForTimeEdit] = useState(false);

  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();
  const { data: remoteColleagues } = useRemoteFetchColleaguesQuery();
  const { data: scheduleColleaguesData } = useRemoteFetchScheduleColleagues();
  const companyIdByUserId = useMemo(
    () => buildCompanyIdByUserId(scheduleColleaguesData),
    [scheduleColleaguesData]
  );
  const { data: companyLocations } = useRemoteFetchCompanyLocations({
    isFinanceRequest: false,
    noMobileWorking: true,
    isBookingSettings: true
  });

  /** Must run before any `return` (including `if (!schedule) return null`) — rules-of-hooks. */
  const isConferenceServiceSetupLock = useConferenceServiceSetupLock(schedule ?? undefined);

  const handleClose = () => {
    setSelectTab(0);
    setBlockCloseForTimeEdit(false);
    onClose();
  };

  const handleDialogClose = createScheduleSelectionDialogCloseHandler(
    blockCloseForTimeEdit,
    handleClose
  );
  const handleTabChange = (e: any, newValue: number) => setSelectTab(newValue);
  const handleEditSubject = async (val: string, schedule: BookingScheduleInterface) => {
    const updateData = {
      noPlaceZoneBookingId: schedule.id,
      subject: val
    };

    return updateNoPlaceZoneBookingScheduleAttributes(updateData)
      .then(() => {
        enqueueSnackbar(t("Successfully edited conference subject"), { variant: "success" });
        refetchAllSchedule?.();
      })
      .catch(() => enqueueSnackbar(t(`Error while editing subject`), { variant: "error" }));
  };

  if (!schedule) return null;

  const { tableSxStyle, dialogStyleTop, dialogStyleWidth } = scheduleSelectionDialogStyle({
    isMobileTablet,
    isMobile,
    theme
  });

  const {
    isUserScheduleDeletable,
    isColleagueScheduleEditable,
    isColleagueCheckInUnlockAllowed,
    checkBookingTypeZone,
    isConferenceZoneBooking
  } = generateScheduleType({
    schedule,
    userInformation,
    remoteColleagues,
    isCompanyAdmin,
    companyIdByUserId
  });

  const isFleetBooking = schedule.bookingType === BookingType.VEHICLE;

  /** Table view opens the same dialog read-only: no colleague edits (conference time/subject/guests, etc.). */
  const dialogContentEditable = isEditable && isCalendarView;

  const dialogColleagueScheduleEditable = isColleagueScheduleEditable && isCalendarView;
  const dialogColleagueCheckInUnlockAllowed = isColleagueCheckInUnlockAllowed && isCalendarView;

  const canCancelBooking =
    isCalendarView && (isUserScheduleDeletable || isColleagueScheduleEditable);
  const showVisitorTab = shouldShowVisitorScheduleTab(schedule);
  let nextTabIndex = 1;
  const invitationTabIndex = checkBookingTypeZone ? nextTabIndex++ : null;
  const servicesTabIndex = isConferenceZoneBooking ? nextTabIndex++ : null;
  const visitorTabIndex = showVisitorTab ? nextTabIndex++ : null;
  const fleetInfoTabIndex = isFleetBooking ? nextTabIndex++ : null;
  const showVisitorHeaderItem = showResourceSensitiveScheduleUi && schedule.isGuest;

  return (
    <Dialog
      key={schedule.id}
      open={isOpen}
      onClose={handleDialogClose}
      data-testid="booking-selection-dialog-parent"
      fullScreen={isMobileTablet}
      slots={{ transition: TransitionForSlideoutPanel }}
      transitionDuration={400}
      slotProps={{
        paper: {
          sx: {
            height: `calc(100vh - ${dialogStyleTop})`,
            maxHeight: `calc(100vh - ${dialogStyleTop})`,
            position: "absolute",
            right: 0,
            top: dialogStyleTop,
            margin: 0,
            maxWidth: dialogStyleWidth,
            width: dialogStyleWidth,
            padding: "20px 40px 40px 40px"
          }
        }
      }}
    >
      {/* dialog header */}
      <Grid2
        data-testid="booking-selection-dialog-header"
        container
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: isMobile ? "wrap" : "nowrap",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          minWidth: 0,
          pb: 0.5
        }}
      >
        {/* schedule status display (hidden for resource view) */}
        {isColleagueMode && schedule.status !== BookingRequestsStatus.APPROVED && (
          <Grid2 sx={{ display: "flex", alignItems: "center", pr: 1 }}>
            <Typography sx={{ fontSize: 12, border: "1px solid black", borderRadius: 1, p: 0.5 }}>
              {t(`${schedule.status}`)}
            </Typography>
          </Grid2>
        )}

        {/* close button — marginLeft: auto keeps it right when status/visitor blocks are hidden */}
        <Grid2
          sx={{
            marginLeft: "auto",
            textAlign: "right",
            alignSelf: "center",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5
          }}
        >
          {/* On desktop/tablet keep participant controls beside close icon. */}
          {!isMobile && showVisitorHeaderItem && (
            <ScheduleEventBookingHeaderVisitorsContents
              userId={userInformation.sub}
              schedule={schedule}
              calendarData={calendarData}
              onCalendarDataChange={onCalendarDataChange}
              onClose={handleClose}
              refetchAllSchedule={refetchAllSchedule}
              canRejectInvitation={isCalendarView}
            />
          )}

          <IconButton data-testid={"close-booking-selection-dialog-btn"} onClick={handleClose}>
            <Close />
          </IconButton>
        </Grid2>

        {/* On mobile place participant controls on the next line below status/close row. */}
        {isMobile && showVisitorHeaderItem && (
          <Grid2 sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
            <ScheduleEventBookingHeaderVisitorsContents
              userId={userInformation.sub}
              schedule={schedule}
              calendarData={calendarData}
              onCalendarDataChange={onCalendarDataChange}
              onClose={handleClose}
              refetchAllSchedule={refetchAllSchedule}
              canRejectInvitation={isCalendarView}
            />
          </Grid2>
        )}
      </Grid2>

      {/* tabs header */}
      <Grid2 container data-testid="booking-selection-dialog-contents-tabs">
        <CustomColoredTabs value={selectTab} onChange={handleTabChange} autoScrollable>
          <CustomColoredTab
            index={0}
            data-testid={"booking-selection-tab0"}
            label={t("Booking")}
            sx={tableSxStyle}
            {...a11yProps(0)}
          />

          {checkBookingTypeZone && (
            <CustomColoredTab
              index={invitationTabIndex!}
              data-testid={"booking-selection-tab1"}
              label={t("Invitation")}
              sx={tableSxStyle}
              {...a11yProps(invitationTabIndex!)}
            />
          )}

          {isConferenceZoneBooking && (
            <CustomColoredTab
              index={servicesTabIndex!}
              data-testid={"booking-selection-tab2"}
              sx={tableSxStyle}
              label={t("Services")}
              {...a11yProps(servicesTabIndex!)}
            />
          )}

          {showVisitorTab && (
            <CustomColoredTab
              index={visitorTabIndex!}
              data-testid={"booking-selection-tab-visitor-info"}
              sx={tableSxStyle}
              label={t("Visitor")}
              {...a11yProps(visitorTabIndex!)}
            />
          )}

          {isFleetBooking && (
            <CustomColoredTab
              index={fleetInfoTabIndex!}
              data-testid={"booking-selection-tab-vehicle-info"}
              sx={tableSxStyle}
              label={t("Vehicle Info")}
              {...a11yProps(fleetInfoTabIndex!)}
            />
          )}
        </CustomColoredTabs>
      </Grid2>

      {/* dialog content */}
      <DialogContent
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            overflowY: "visible",
            padding: 0
          }
        ]}
      >
        <Grid2 container data-testid="booking-selection-dialog-contents">
          {/* tabPanel - booking */}
          <TabPanel value={selectTab} index={0}>
            <ScheduleSelectionBookingTab
              isMobileTablet={isMobileTablet}
              isMobile={isMobile}
              isEditable={dialogContentEditable}
              isColleagueScheduleEditable={dialogColleagueScheduleEditable}
              isColleagueCheckInUnlockAllowed={dialogColleagueCheckInUnlockAllowed}
              isConferenceServiceSetupLock={isConferenceServiceSetupLock}
              schedule={schedule}
              schedulePictures={schedulePictures}
              pictureIsLoading={pictureIsLoading}
              userInformation={userInformation}
              bookingUser={remoteUsers?.find(user => user.userId === schedule?.userBookedFor)}
              bookedByUser={
                schedule?.userBookedBy && schedule.userBookedBy !== schedule?.userBookedFor
                  ? remoteUsers?.find(user => user.userId === schedule.userBookedBy)
                  : undefined
              }
              allSchedules={allSchedules}
              calendarData={calendarData}
              isFirstAidSelected={isFirstAidSelected}
              handleEditSubject={handleEditSubject}
              onScheduleChange={onScheduleChange}
              onClickUnlock={onClickUnlock}
              refetchAllSchedule={refetchAllSchedule}
              setCalendarData={setCalendarData}
              showResourceSensitiveBookingDetails={showResourceSensitiveScheduleUi}
              isColleagueMode={isColleagueMode}
              onTimeEditBlockingUiChange={setBlockCloseForTimeEdit}
            />
          </TabPanel>

          {/* tabPanel - Invitation */}
          {checkBookingTypeZone && (
            <TabPanel value={selectTab} index={invitationTabIndex!}>
              <ScheduleSelectionInviteVisitorsTab
                isMobileTablet={isMobileTablet}
                schedule={schedule}
                isEditable={dialogContentEditable}
                isColleagueScheduleEditable={dialogColleagueScheduleEditable}
                refetchAllSchedule={refetchAllSchedule}
              />
            </TabPanel>
          )}

          {isConferenceZoneBooking && (
            <TabPanel value={selectTab} index={servicesTabIndex!}>
              <ScheduleSelectionServicesTab
                schedule={schedule as NoPlaceZoneBooking}
                bookingUser={remoteUsers?.find(user => user.userId === schedule?.userBookedFor)}
                isMobileTablet={isMobileTablet}
                isEditable={isUserScheduleDeletable && isCalendarView}
                refetchAllSchedule={refetchAllSchedule}
              />
            </TabPanel>
          )}

          {showVisitorTab && (
            <TabPanel value={selectTab} index={visitorTabIndex!}>
              <ScheduleSelectionVisitorCardContents
                schedule={schedule}
                isDialogEditable={dialogContentEditable}
                isColleagueScheduleEditable={dialogColleagueScheduleEditable}
                locations={companyLocations}
                onSaved={refetchAllSchedule}
              />
            </TabPanel>
          )}

          {isFleetBooking && (
            <TabPanel value={selectTab} index={fleetInfoTabIndex!}>
              {schedule.vehicleInfo ? (
                <VehicleInformationCardContents
                  vehicleInfo={schedule.vehicleInfo}
                  isCalendarDisplay={true}
                />
              ) : (
                <Typography color="text.secondary">
                  {t("No vehicle information available")}
                </Typography>
              )}
            </TabPanel>
          )}
        </Grid2>
      </DialogContent>

      {/* dialog actions */}
      <DialogActions sx={[isMobile ? { mb: 6 } : { mb: 0 }]}>
        {/* cancel booking */}
        {canCancelBooking && (
          <ScheduleEventDeleteButton
            schedule={schedule}
            calendarData={calendarData}
            onCalendarDataChange={onCalendarDataChange}
            onClose={handleClose}
            setDeletingBookings={setDeletingBookings}
            isEditable={isEditable}
            isConferenceServiceSetupLock={isConferenceServiceSetupLock}
            handleRejectBookings={handleRejectBookings}
            handleApproveBookings={handleApproveBookings}
            refetchAllSchedule={refetchAllSchedule}
          />
        )}
        {isMobile && (
          <Button color="secondary" onClick={handleClose}>
            {t("Close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
